import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { readRecentOrders } from "@/lib/recentOrders";

const COINS = [["BTC", "Bitcoin"], ["LTC", "Litecoin"]];

// Offers to merge every unpaid order the visitor can prove ownership of into one crypto payment.
// mode "browser": uses the order id+token pairs remembered in this browser (anonymous checkout).
// mode "account": uses the logged-in account's unpaid orders.
export const CombineUnpaid = ({ mode = "browser", currentOrderId = null, minOrders = 2 }) => {
  const nav = useNavigate();
  const [unpaid, setUnpaid] = useState([]);
  const [picked, setPicked] = useState({});
  const [coin, setCoin] = useState("BTC");
  const [busy, setBusy] = useState(false);
  const tokens = useMemo(() => Object.fromEntries(readRecentOrders().map((o) => [o.id, o.token])), []);

  useEffect(() => {
    const req = mode === "account"
      ? api.get("/my/unpaid-orders")
      : (() => {
        const refs = readRecentOrders().map((o) => ({ id: o.id, token: o.token }));
        return refs.length ? api.post("/payments/unpaid-lookup", { orders: refs }) : Promise.resolve({ data: [] });
      })();
    req.then((r) => {
      const list = r.data || [];
      setUnpaid(list);
      setPicked(Object.fromEntries(list.map((o) => [o.id, true])));
      if (list.length) setCoin(list[0].currency === "LTC" ? "LTC" : "BTC");
    }).catch(() => setUnpaid([]));
  }, [mode]);

  const chosen = unpaid.filter((o) => picked[o.id]);
  const total = chosen.reduce((s, o) => s + Number(o.total || 0), 0);
  if (unpaid.length < minOrders) return null;

  const combine = async () => {
    if (chosen.length < 1) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/combine", {
        orders: chosen.map((o) => ({ id: o.id, token: tokens[o.id] || null })),
        currency: coin,
      });
      nav(`/pay/${data.id}?token=${encodeURIComponent(data.access_token)}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border border-primary/40 bg-primary/5 p-5" data-testid="combine-unpaid">
      <div className="flex items-center gap-2 font-heading text-base font-bold">
        <Layers className="h-4 w-4 text-primary" /> Pay your unpaid orders together
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        You have {unpaid.length} orders awaiting payment. Merge them into one payment so you only send crypto once —
        each order keeps its own order number and tracking PIN.
      </p>
      <div className="mt-3 divide-y divide-border border border-border bg-background">
        {unpaid.map((o) => (
          <label key={o.id} className="flex cursor-pointer items-center gap-3 p-3 text-sm" data-testid={`combine-order-${o.order_number}`}>
            <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!picked[o.id]}
              onChange={(e) => setPicked({ ...picked, [o.id]: e.target.checked })} data-testid={`combine-check-${o.order_number}`} />
            <span className="mono font-semibold">{o.order_number}</span>
            {o.id === currentOrderId && <span className="mono text-[10px] uppercase text-primary">this order</span>}
            <span className="ml-auto mono">{gbp(o.total)}</span>
          </label>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex border border-border" data-testid="combine-coin">
          {COINS.map(([c, name]) => (
            <button key={c} type="button" onClick={() => setCoin(c)} data-testid={`combine-coin-${c}`}
              className={`px-3 py-2 text-xs font-medium ${coin === c ? "bg-foreground text-background" : "hover:bg-muted"}`}>{name}</button>
          ))}
        </div>
        <button onClick={combine} disabled={busy || chosen.length < 1} data-testid="combine-submit"
          className="inline-flex items-center gap-2 bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
          Pay {chosen.length} order{chosen.length === 1 ? "" : "s"} together · {gbp(total)}
        </button>
      </div>
    </div>
  );
};
